import { Injectable, OnDestroy } from "@angular/core";
import { BehaviorSubject, Observable, Subject, Subscription } from "rxjs";
import { IdsForSettingsService } from "./ids-for-settings.service";
import { PermissionGroup } from "src/app/constants/settings.enum";
import { ToastService } from "src/app/services/toast.service";
import { PermissionsApiClient } from "src/app/services/api-clients/permissions.apiclient";
import { PermissionModel } from "src/app/models/api/permission.model";
import { LevelEnum } from "src/app/constants/level.enum";
@Injectable({
    providedIn: 'root'
})
export class PermissionsService implements OnDestroy {
    private permissionsUpdated$: BehaviorSubject<boolean> = new BehaviorSubject(false);
    /**
     * Returns true IF settings have LOADED
     */
    public get permissionsUpdated(): Observable<any> { return this.permissionsUpdated$.asObservable(); }
    private Permissions: PermissionModel[] = [];
    private orgIdSubscription: Subscription;
    private selectedOrganisationId: string = "";
    private selectedWhitelabelId: string = "";
    constructor(
        private idsForSettingService: IdsForSettingsService,
        private permissionApiClient: PermissionsApiClient,
        private toastService: ToastService) {
        this.orgIdSubscription = this.idsForSettingService.selectedOrganisationId$.subscribe(orgId => {
            if (!orgId || orgId === this.selectedOrganisationId) {
                return;
            }
            this.refreshForOrganisation(orgId);
        });
    }
    /**
     * 
     * @param organisationId 
     * @param forceRefreshCacheButDontEmit Whether even IF the OrganisationId is the same, still retrieve new Permissions and thus refresh cache. 
     *                                     BUT DO NOT PUSH THEM
     */
    public refreshForOrganisation(organisationId: string, forceRefreshCacheButDontEmit: boolean = false) {
        this.selectedOrganisationId = organisationId;
        this.permissionApiClient.getOrganisationPermissions(organisationId, forceRefreshCacheButDontEmit).subscribe(permissions => {
            // when refreshing the cache (IN API CLIENT) the permissions SHOULD NOT BE EMITTED/PUSHED
            if (forceRefreshCacheButDontEmit) {
                return;
            }
            this.Permissions = permissions;
            this.permissionsUpdated$.next(true);
        });
    }

    public refreshForWhitelabel(whitelabelId: string) {
        this.selectedWhitelabelId = whitelabelId;
        this.permissionApiClient.getWhitelabelPermissions(whitelabelId).subscribe(permissions => {
            this.Permissions = permissions;
            this.permissionsUpdated$.next(true);
        });
    }

    public refreshForSystem() {
        this.permissionApiClient.getSystemPermissions().subscribe(permissions => {
            this.Permissions = permissions;
            this.permissionsUpdated$.next(true);
        });
    }

    ngOnDestroy(): void {
        if (this.orgIdSubscription) {
            this.orgIdSubscription.unsubscribe();
        }
    }

    public UpdatePermission(permissionName: string, newValue: boolean, level: LevelEnum) {
        var existingPermissionIndex = this.Permissions.findIndex(x => x.name == permissionName);
        if (existingPermissionIndex > 0) {
            this.Permissions[existingPermissionIndex].value = newValue.toString();
            this.Permissions[existingPermissionIndex].valueAsBoolean = newValue;
        }

        let levelId = this.getIdAssociatedWithLevel(level);
        this.permissionApiClient.updatePermissionValue(levelId, permissionName, newValue, level).subscribe(() => {
            this.toastService.showSaved();
        });
    }

    public UpdatePermissionConfiguration(permissionName: string, level: LevelEnum, permissionIsDisabled: boolean | null = null, permissionIsHidden: boolean | null = null, permissionIsLocked: boolean | null = null) {
        var existingPermissionIndex = this.Permissions.findIndex(x => x.name == permissionName);
        if (existingPermissionIndex > 0) {
            if (permissionIsDisabled != null) {
                this.Permissions[existingPermissionIndex].isDisabled = permissionIsDisabled;
            }
            if (permissionIsHidden != null) {
                this.Permissions[existingPermissionIndex].isHidden = permissionIsHidden;
            }
            if (permissionIsLocked != null) {
                this.Permissions[existingPermissionIndex].isLocked = permissionIsLocked;
            }
        }

        let levelId = this.getIdAssociatedWithLevel(level);
        this.permissionApiClient.updatePermissionConfiguration(levelId, permissionName, level, permissionIsDisabled, permissionIsHidden, permissionIsLocked).subscribe();
    }

    public ForcePermissionValueAndConfiguration(permissionName: string, level: LevelEnum) {
        let levelId = this.getIdAssociatedWithLevel(level);
        this.permissionApiClient.forcePermissionValueAndConfiguration(levelId, permissionName, level).subscribe(() => {
            // we want the newest force to also display for lower levels
            if (this.selectedOrganisationId) {
                // we must refresh the cache, since the id doesnt change this doesnt happen otherwise
                // but we do not want to emit permissions event (since we force from a NON organisation Page)
                var refreshCacheButDoNotEmitEvent = true;
                this.refreshForOrganisation(this.selectedOrganisationId, refreshCacheButDoNotEmitEvent);
            }
            this.toastService.showSuccessByKey('Toasts.Success', 'Toasts.SuccesfullForce');
        });
    }

    public GetPermission(name: string): PermissionModel {
        var existingPermissionIndex = this.Permissions.findIndex(x => x.name == name);
        if (existingPermissionIndex >= 0) {
            return this.Permissions[existingPermissionIndex];
        }
        var allowedPermission = new PermissionModel();
        allowedPermission.valueAsBoolean = true
        return allowedPermission;
    }



    public GetPermissions(permissionGroup: PermissionGroup): PermissionModel[] {
        var matchingPermissions = this.Permissions.filter(x => x.configurationGroup == permissionGroup);
        return matchingPermissions;
    }

    private getIdAssociatedWithLevel(level: LevelEnum) {
        let levelId: string = "";
        if (level === LevelEnum.OrganisationLevel) levelId = this.selectedOrganisationId;
        if (level === LevelEnum.WhitelabelLevel) levelId = this.selectedWhitelabelId;
        // SYSTEM is singular so no Id is needed
        return levelId;
    }
}